import classNames from 'classnames';
import { UIEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaGripLines } from 'react-icons/fa6';
import { RiArrowDownSFill } from 'react-icons/ri';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { SpaceDiscriminator } from '@client/api';
import { IconButton, isDefinedString, Text } from '@client/toolkit';
import { NodeModel } from '@minoru/react-dnd-treeview';

import { PortfolioPaths } from '../../portfolio.router';
import { SpaceNodeData } from '../hooks/use-space-tree-data';
import { SpaceTreeTag, SpaceTreeTagColor, useSpaceTreeTags } from '../hooks/use-space-tree-tags';
import { useState } from 'react';
import { SpaceSideMenuPermissionBasedCta } from './space-sidemenu-permission-based-cta';

type SpaceNodeHighlighterProps = {
  color: SpaceTreeTagColor;
  enabled: boolean;
};

function SpaceNodeHighlighter({ color, enabled }: SpaceNodeHighlighterProps) {
  return (
    <div
      className={classNames(
        'w-3 h-3 rounded-full',
        !enabled && 'hidden',
        color === 'teal' && 'bg-brand-primary-active',
      )}
    />
  );
}

type SpaceNodeProps = {
  node: NodeModel<SpaceNodeData>;
  depth: number;
  isOpen: boolean;
  hasChild: boolean;
  onToggle: (id: NodeModel['id']) => void;
  isDropTarget: boolean;
  draggable: boolean;
  isFetching?: boolean;
};

export function SpaceNode({
  node,
  depth,
  isOpen,
  onToggle,
  hasChild,
  isDropTarget,
  draggable,
  isFetching,
}: SpaceNodeProps) {
  const [isHover, setIsHover] = useState(false);
  const { t: tType } = useTranslation('type');

  const { buildingId, spaceId } = useParams<{ buildingId: string; spaceId: string }>();
  const navigate = useNavigate();

  const item = useMemo(() => node.data?.item, [node.data?.item]);
  const discriminator = useMemo(() => item?.discriminator, [item?.discriminator]);

  const path = useMemo(() => {
    if (item == null || !isDefinedString(item.id) || !isDefinedString(discriminator)) {
      return;
    }

    switch (discriminator) {
      case 'Building':
        return generatePath(PortfolioPaths.Building, { buildingId: item.id });
      default:
        return generatePath(PortfolioPaths.Space, { buildingId, spaceId: item.id, discriminator });
    }
  }, [buildingId, discriminator, item]);

  const handleSelectItem = () => {
    if (!isDefinedString(path)) {
      return;
    }

    navigate(path, { replace: true });
  };

  const handleClickCollapse = (event: UIEvent) => {
    if (!hasChild) return;
    onToggle(node.id);
    event.stopPropagation();
  };

  const isActive = useMemo(() => {
    if (item?.id == null) return false;

    if (spaceId == null) {
      return buildingId === item.id;
    } else {
      return spaceId === item.id;
    }
  }, [buildingId, item?.id, spaceId]);

  const itemRef = useRef<HTMLDivElement>(null);

  // scroll to active element when the active element changes
  useEffect(() => {
    if (!isActive) return;
    if (itemRef.current != null) {
      itemRef.current.scrollIntoView(false);
    }
  }, [isActive]);

  const { isTagActive, TagOptions } = useSpaceTreeTags();

  // check if a node can be highlighted by an active tag
  const isHighlighterEnabled = useCallback(
    (tag: SpaceTreeTag) => {
      if (!isTagActive(tag)) return false;
      const rentableDiscriminators: SpaceDiscriminator[] = ['Building', 'CommercialSpace', 'ResidentialUnit'];

      switch (tag) {
        case 'HighlightRentables':
          return !!discriminator && rentableDiscriminators.includes(discriminator);
        default:
          return false;
      }
    },
    [discriminator, isTagActive],
  );

  // check if any highlighter is enabled
  const isHighlighted = useMemo(() => {
    return TagOptions.findIndex((x) => isHighlighterEnabled(x.value)) >= 0;
  }, [TagOptions, isHighlighterEnabled]);

  return (
    <div
      ref={itemRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      className={classNames(
        'relative px-1 py-0.5 text-sm hover:bg-brand-primary-hover hover:text-black rounded-md cursor-pointer group overflow-hidden flex items-center',
        isActive && !isDropTarget && 'bg-brand-primary',
        isDropTarget && 'bg-brand-primary',
        isFetching && 'pointer-events-none',
        !isActive && 'text-white/[0.9]',
      )}
      style={{ marginLeft: 16 * depth }}
      onClick={handleSelectItem}
    >
      <IconButton
        color="none"
        size="sm"
        fontSize="md"
        rounding="none"
        disabled={!hasChild}
        className={classNames(
          '-my-1 mr-2',
          isActive || isHover ? 'text-black hover:text-black' : 'text-brand-primary hover:text-black/[0.9]',
          !hasChild && 'invisible opacity-0',
        )}
        onClick={handleClickCollapse}
      >
        <RiArrowDownSFill className={classNames('transition-transform duration-150', !isOpen && '-rotate-90')} />
      </IconButton>
      <Text
        className="whitespace-nowrap font-medium flex-1"
        as="div"
        isEmpty={!isDefinedString(node.text)}
        loading={isFetching}
        title={node.text}
      >
        {node.text}
      </Text>
      {!isFetching && (
        <>
          {isHighlighted && (
            <div className="flex items-center justify-end ml-2 mr-2 space-x-1">
              {TagOptions.map((tag) => (
                <SpaceNodeHighlighter key={tag.value} color={tag.color} enabled={isHighlighterEnabled(tag.value)} />
              ))}
            </div>
          )}
          <Text
            as="div"
            isEmpty={!isDefinedString(discriminator)}
            title={tType(`SpaceDiscriminator.${discriminator}`) ?? undefined}
            className={classNames(
              'whitespace-nowrap group-hover:text-white/[0.9] tracking-wide text-right group-hover:hidden mr-2',
              isActive ? 'text-black/[0.9]' : 'text-white/[0.6] font-light',
            )}
          >
            {tType(`SpaceDiscriminator.${discriminator}`)}
          </Text>
          <SpaceSideMenuPermissionBasedCta item={node.data?.item} />
        </>
      )}
      <div
        className={classNames(
          'hidden',
          'absolute left-0 right-0 top-0 bottom-0 flex justify-center items-center invisible group-hover:visible opacity-0 group-hover:opacity-70 pointer-events-none',
          'text-lg text-white/[0.70]',
          !draggable && 'hidden',
        )}
      >
        <FaGripLines />
      </div>
    </div>
  );
}
